// src/contexts/ChatContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

function getStoredUserMeta() {
  if (typeof window === 'undefined') return { id: 0, isClient: false };
  try {
    const raw = window.localStorage.getItem('currentUser') || window.localStorage.getItem('user');
    if (!raw) return { id: 0, isClient: false };
    const parsed = JSON.parse(raw);
    const id = Number(parsed?.userid || 0) || 0;
    const flag = parsed?.isclient ?? parsed?.isClient;
    const normalized = typeof flag === 'string' ? flag.trim().toLowerCase() : flag;
    const isClient = normalized === true || normalized === 'true' || normalized === '1' || Number(normalized) === 1;
    return { id, isClient: !!isClient };
  } catch {
    return { id: 0, isClient: false };
  }
}

const ChatContext = createContext();

export function ChatProvider({ children }) {
  // Helper: does a signed session cookie exist? (server-side auth gate)
  const hasSessionCookie = useCallback(() => {
    try {
      const c = document.cookie || '';
      return /(__Host-e3pl\.sid|\bsession=)/.test(c);
    } catch { return false; }
  }, []);
  // Agent window state (existing)
  const [isChatOpen, setIsChatOpen] = useState(() => {
    try { return localStorage.getItem('chat.agent.open') === '1'; } catch { return false; }
  });
  // New DM window state
  const [isDmOpen, setIsDmOpen] = useState(() => {
    try { return localStorage.getItem('chat.dm.open') === '1'; } catch { return false; }
  });
  const [activeDmUserId, setActiveDmUserId] = useState(() => {
    try {
      const v = localStorage.getItem('chat.dm.activeUserId');
      const n = Number(v || 0);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch { return null; }
  });
  const [unreadByUser, setUnreadByUser] = useState({}); // { userId: count }
  const [pushStatus, setPushStatus] = useState('disconnected'); // 'ws' | 'sse' | 'disconnected'
  const [userMeta, setUserMeta] = useState(() => getStoredUserMeta());
  const { id: selfUserId, isClient: isClientUser } = userMeta;

  // Derived total unread count
  const unreadCount = useMemo(() => Object.values(unreadByUser).reduce((a, b) => a + (Number(b) || 0), 0), [unreadByUser]);

  // Refs to avoid recreating SSE on minor state changes
  const isChatOpenRef = useRef(isChatOpen);
  const isDmOpenRef = useRef(isDmOpen);
  const activeDmRef = useRef(activeDmUserId);
  const userIdRef = useRef(selfUserId);
  const isClientRef = useRef(isClientUser);
  // Live transport refs so we can close on logout/tab events immediately
  const wsRef = useRef(null);
  const sseRef = useRef(null);
  const handledMsgIdsRef = useRef(new Set());
  useEffect(() => { isChatOpenRef.current = isChatOpen; }, [isChatOpen]);
  useEffect(() => { isDmOpenRef.current = isDmOpen; }, [isDmOpen]);
  useEffect(() => { activeDmRef.current = activeDmUserId; }, [activeDmUserId]);
  useEffect(() => { userIdRef.current = selfUserId; }, [selfUserId]);
  useEffect(() => { isClientRef.current = isClientUser; }, [isClientUser]);

  const syncUserMeta = useCallback(() => {
    setUserMeta(getStoredUserMeta());
  }, []);

  // Persist window open states and selected DM user
  useEffect(() => {
    // If not logged in or no session cookie, do not attempt to connect or seed presence
    if (!selfUserId || Number(selfUserId) <= 0 || !hasSessionCookie()) {
      setPushStatus('disconnected');
      return () => {};
    }
    try { localStorage.setItem('chat.agent.open', isChatOpen ? '1' : '0'); } catch {}
  }, [isChatOpen]);
  useEffect(() => {
    try { localStorage.setItem('chat.dm.open', isDmOpen ? '1' : '0'); } catch {}
  }, [isDmOpen]);
  useEffect(() => {
    try {
      if (activeDmUserId && Number.isFinite(Number(activeDmUserId))) {
        localStorage.setItem('chat.dm.activeUserId', String(activeDmUserId));
      } else {
        localStorage.removeItem('chat.dm.activeUserId');
      }
    } catch {}
  }, [activeDmUserId]);

  // Close chat windows on logout or session expire
  useEffect(() => {
    const closeAll = () => {
      try {
        setIsChatOpen(false);
        setIsDmOpen(false);
        setActiveDmUserId(null);
        setUnreadByUser({});
        setPushStatus('disconnected');
        setUserMeta({ id: 0, isClient: false });
        localStorage.setItem('chat.agent.open', '0');
        localStorage.setItem('chat.dm.open', '0');
        localStorage.removeItem('chat.dm.activeUserId');
        try { wsRef.current && wsRef.current.readyState <= 1 && wsRef.current.close(1000, 'logout'); } catch {}
        try { sseRef.current && sseRef.current.close && sseRef.current.close(); } catch {}
      } catch {}
    };
    const onLogout = () => closeAll();
    const onStorage = (e) => {
      if (!e) return;
      const k = e.key || '';
      if (k === 'user' || k === 'authToken') {
        closeAll();
        if (e.newValue) syncUserMeta();
      }
    };
    window.addEventListener('auth:logout', onLogout);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('auth:logout', onLogout);
      window.removeEventListener('storage', onStorage);
    };
  }, [syncUserMeta]);

  useEffect(() => {
    const onLogin = () => syncUserMeta();
    window.addEventListener('auth:login', onLogin);
    return () => window.removeEventListener('auth:login', onLogin);
  }, [syncUserMeta]);

  // Global WebSocket for messages/presence (fallback to SSE if WS fails) with defer + backoff
  useEffect(() => {
    const myId = Number(selfUserId || 0);
    const blockChat = isClientUser;
    // Also gate on the presence of a valid session cookie so the login page doesn't connect
    if (!myId || blockChat || !hasSessionCookie()) {
      setPushStatus('disconnected');
      return undefined;
    }
    // Default to WebSocket for exact presence unless explicitly disabled
    const USE_WS = String(import.meta.env.VITE_CHAT_USE_WS ?? '1').trim() !== '0';
    const base = import.meta.env.VITE_API_URL || '';
    const wsUrl = (() => {
      try {
        if (base) {
          const u = new URL(base, window.location.href);
          u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
          u.pathname = (u.pathname.replace(/\/$/, '')) + '/api/messages/ws';
          return u.toString();
        }
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
        return `${proto}://${window.location.host}/api/messages/ws`;
      } catch {
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
        return `${proto}://${window.location.host}/api/messages/ws`;
      }
    })();

    let ws;
    let sse;
    let usingWs = false;
    let closed = false;
    let attempts = 0;

    const onMsg = (payload) => {
      try {
        const m = payload || {};
        if (!m || typeof m !== 'object') return;
        const mid = Number(m.id || 0) || 0;
        if (mid) {
          const seen = handledMsgIdsRef.current;
          if (seen.has(mid)) return; // dedupe same message from WS+SSE
          try {
            seen.add(mid);
            if (seen.size > 2000) handledMsgIdsRef.current = new Set(Array.from(seen).slice(-1000));
          } catch {}
        }
        if (Number(m.recipient_id) === myId) {
          const sender = Number(m.sender_id || 0) || 0;
          const viewing = isDmOpenRef.current && activeDmRef.current === sender;
          if (!viewing && sender) setUnreadByUser(prev => ({ ...prev, [sender]: (prev[sender] || 0) + 1 }));
        }
        window.dispatchEvent(new CustomEvent('chat:newMessage', { detail: { message: m } }));
      } catch {}
    };

    const onPresence = (arr) => {
      try {
        window.dispatchEvent(new CustomEvent('chat:presence', { detail: { online: arr } }));
      } catch {}
    };

    const connectWS = () => {
      if (closed) return;
      attempts += 1;
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => {
          usingWs = true; attempts = 0; setPushStatus('ws');
          // If SSE fallback was started, close it once WS is healthy to avoid duplicate events
          try { if (sseRef.current && typeof sseRef.current.close === 'function') { sseRef.current.close(); sseRef.current = null; } } catch {}
        };
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data || '{}');
            if (msg?.event === 'message') onMsg(msg.data);
            else if (msg?.event === 'presence') onPresence(msg.data?.online || []);
            else if (msg?.event === 'attachment') {
              try { window.dispatchEvent(new CustomEvent('chat:attachment', { detail: { attachment: msg.data } })); } catch {}
            }
          } catch {}
        };
        ws.onerror = () => {};
        ws.onclose = () => {
          if (closed) return;
          usingWs = false;
          setPushStatus('disconnected');
          // Backoff reconnect a few times before falling back
          const delay = Math.min(1000 * attempts, 4000); // 1s, 2s, 3s, 4s caps
          setTimeout(() => {
            if (attempts < 4) connectWS(); else sseFallback();
          }, delay);
        };
      } catch {
        if (attempts < 2) setTimeout(connectWS, 600);
        else sseFallback();
      }
    };

    // Fallback SSE if WS not usable
    const sseFallback = () => {
      try {
        if (sse) return; // already active
        const src = new EventSource(`${base}/api/messages/stream`, { withCredentials: true });
        sse = src;
        sseRef.current = src;
        setPushStatus('sse');
        src.addEventListener('message', (e) => {
          try { onMsg(JSON.parse(e.data || '{}')); } catch {}
        });
        src.addEventListener('presence', (e) => {
          try { const d = JSON.parse(e.data || '{}'); onPresence(Array.isArray(d.online) ? d.online : []); } catch {}
        });
        src.addEventListener('attachment', (e) => {
          try { const d = JSON.parse(e.data || '{}'); window.dispatchEvent(new CustomEvent('chat:attachment', { detail: { attachment: d } })); } catch {}
        });
        src.onerror = () => { try { setPushStatus('disconnected'); } catch {} };
      } catch {}
    };

    // Defer initial connection until after window load to avoid
    // "connection interrupted while the page was loading" noise (Firefox)
    const start = () => {
      if (closed) return;
      if (USE_WS) {
        connectWS();
        // After a short grace period, if WS still not up, start SSE quickly
        setTimeout(() => { if (!usingWs) sseFallback(); }, 200);
      } else {
        sseFallback();
      }
      // Seed presence immediately via REST for instant UI (only when authenticated)
      try {
        if (hasSessionCookie()) {
          const base = import.meta.env.VITE_API_URL || '';
          fetch(`${base}/api/messages/online`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.resolve({ online: [] }))
            .then(j => { try { window.dispatchEvent(new CustomEvent('chat:presence', { detail: { online: j?.online || [] } })); } catch {} })
            .catch(()=>{});
        }
      } catch {}
    };
    // Connect after window load for stability across refreshes
    let addedLoadListener = false;
    const onLoad = () => start();
    if (document.readyState === 'complete') start();
    else { window.addEventListener('load', onLoad, { once: true }); addedLoadListener = true; }

    return () => {
      closed = true;
      try { ws && ws.close(); } catch {}
      try { sse && sse.close && sse.close(); } catch {}
      try { if (wsRef.current && wsRef.current !== ws) wsRef.current.close(); } catch {}
      try { if (sseRef.current && sseRef.current !== sse && sseRef.current.close) sseRef.current.close(); } catch {}
      setPushStatus('disconnected');
      if (addedLoadListener) {
        try { window.removeEventListener('load', onLoad); } catch {}
      }
    };
  }, [selfUserId, isClientUser]);

  // API to clear unread for a specific user thread
  const markThreadRead = (userId) => {
    const uid = Number(userId || 0) || 0;
    if (!uid) return;
    setUnreadByUser(prev => {
      if (!prev[uid]) return prev;
      const next = { ...prev };
      delete next[uid];
      return next;
    });
  };

  return (
    <ChatContext.Provider value={{
      // Agent
      isChatOpen, setIsChatOpen,
      // DM
      isDmOpen, setIsDmOpen,
      activeDmUserId, setActiveDmUserId,
      unreadByUser, unreadCount, markThreadRead,
      pushStatus
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    console.error('useChat must be used within a ChatProvider'); // Error if context is missing
  }
  return context;
}
